import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import content from './content.json';
import BackgroundFilm from './BackgroundFilm';
import BackgroundVisualization from './BackgroundVisualization';
import ContactForm from './ContactForm';
import MissionTimeline from './MissionTimeline';
import useScrollScene from './useScrollScene';
import { useReducedMotion } from './useMotion';
import './Home.scss';

function Brand({ onClick }) {
  return (
    <Link className="brand" to="/" onClick={onClick} aria-label={content.brand.homeLabel}>
      <img src={content.brand.logo} alt={content.brand.name} width="1667" height="1668" fetchpriority="high" />
    </Link>
  );
}

function Reveal({ as: Element = 'p', reading = false, children, ...props }) {
  return <Element {...props} data-reveal={reading ? undefined : true} data-reading-line={reading ? true : undefined}><span className="reveal__content">{children}</span></Element>;
}

function StoryLine({ line }) {
  let text = line.text;
  if (line.emphasis && line.text.includes(line.emphasis)) {
    const index = line.text.indexOf(line.emphasis);
    text = <>{line.text.slice(0, index)}<em>{line.emphasis}</em>{line.text.slice(index + line.emphasis.length)}</>;
  }
  return <Reveal className="story-line type-h1" reading data-visual-scene={line.scene}>{text}</Reveal>;
}

function AudienceMedia({ item }) {
  return (
    <div className="audiences__media">
      <img className="audiences__still" src={item.media} alt={item.alt} width="1024" height="1024" loading="lazy" decoding="async" />
    </div>
  );
}

function Organizations({ id, section }) {
  return (
    <section className={`site-section organizations organizations--${id}`} id={id} aria-labelledby={`${id}-heading`}>
      <Reveal as="h2" className="section-heading" id={`${id}-heading`}>{section.heading}</Reveal>
      <ul className="organizations__grid">
        {section.items.map((item, index) => (
          <li className={`organizations__item organizations__item--${index + 1}`} key={item.name}>
            {item.companionLogo ? (
              <div className="organizations__lockup" role="img" aria-label={item.name}>
                <img src={item.companionLogo} alt="" loading="lazy" decoding="async" />
                <img src={item.logo} alt="" loading="lazy" decoding="async" />
              </div>
            ) : <img src={item.logo} alt={item.name} loading="lazy" decoding="async" />}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Home() {
  const root = useRef(null);
  const brandSpace = useRef(null);
  const story = useRef(null);
  const science = useRef(null);
  const visualization = useRef({ phases: {}, filmOpacity: 0 });
  const reducedMotion = useReducedMotion();
  useScrollScene(root, brandSpace, science, visualization);
  useEffect(() => { document.title = content.meta.title; }, []);

  const returnToTop = event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  };
  const skipToStory = () => {
    story.current.focus({ preventScroll: true });
    story.current.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="dynamoid-site" ref={root}>
      <button className="skip-link" type="button" onClick={skipToStory}>{content.accessibility.skipLink}</button>
      <Brand onClick={returnToTop} />
      <BackgroundVisualization sceneRef={visualization} />
      <BackgroundFilm content={content.science} sectionRef={science} />
      <main>
        <section className="site-section hero" aria-label={content.brand.name}>
          <div className="hero__content">
            <div className="hero__brand-space" ref={brandSpace} aria-hidden="true" />
            <h1 className="hero__statement" aria-label={content.hero.accessibleTitle}>
              <span>{content.hero.from}</span>
              <img className="hero__arrow" src={content.hero.arrow} alt="" />
              <span>{content.hero.to}</span>
            </h1>
          </div>
        </section>

        <section className="site-section story" id="story" ref={story} tabIndex={-1} aria-labelledby="story-heading">
          <h2 className="visually-hidden" id="story-heading">{content.story.label}</h2>
          <div className="story__lines">
            {content.story.lines.map(line => <StoryLine key={line.text} line={line} />)}
          </div>
        </section>

        <section className="site-section audiences" id="audiences" aria-labelledby="audiences-heading">
          <h2 className="visually-hidden" id="audiences-heading">{content.audiences.label}</h2>
          <ul className="audiences__grid">
            {content.audiences.items.map(item => (
              <li className="audiences__item" key={item.name} data-pop>
                <div className="pop__content audiences__content">
                  <AudienceMedia item={item} />
                  <h3 className="type-h1">{item.name}</h3>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="site-section science" id="science" ref={science} aria-labelledby="science-heading">
          <h2 className="science__logo" id="science-heading">
            <span className="science__logo-space">
              <a className="science__logo-link" href={content.science.url} target="_blank" rel="noopener noreferrer">
                <img src={content.science.logo} alt={content.science.name} width="959" height="455" loading="lazy" />
              </a>
            </span>
          </h2>
          <div className="science__copy">
            {content.science.lines.map(line => <Reveal key={line} className="type-h1" reading>{line}</Reveal>)}
          </div>
        </section>

        <Organizations id="partners" section={content.partners} />
        <Organizations id="funders" section={content.funders} />

        <section className="site-section team" id="team" aria-labelledby="team-heading">
          <Reveal as="h2" className="section-heading" id="team-heading">{content.team.heading}</Reveal>
          <div className="team__grid">
            {content.team.members.map(member => (
              <article className="team__member" key={member.name}>
                <img className="team__portrait" src={member.portrait} alt={member.name} width="240" height="240" loading="lazy" decoding="async" />
                <Reveal as="h3">{member.name}</Reveal>
                <Reveal className="team__role type-h4">{member.role}</Reveal>
                <Reveal className="team__bio">{member.bio}</Reveal>
              </article>
            ))}
          </div>
        </section>

        <section className="site-section contact" id="contact" aria-labelledby="contact-heading">
          <Reveal as="h2" className="section-heading" id="contact-heading">{content.contact.heading}</Reveal>
          <ContactForm content={content.contact} />
        </section>

        <MissionTimeline content={content.mission} />
      </main>
      <footer className="site-footer">
        <nav aria-label={content.footer.label}>
          {content.footer.links.map(link => (
            <a key={link.href} href={link.href}>{link.label}</a>
          ))}
        </nav>
      </footer>
    </div>
  );
}
